import { useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { ExternalLink, X } from 'lucide-react';
import { MARKETPLACE_CATEGORIES, type MarketplaceCategoryId } from '@claudafinil/shared';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select } from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import type {
  ApiResult,
  MarketplacePromptResponse,
  PublishMarketplacePromptInput,
} from '../../../lib/api';
import { SITE_BASE_URL } from '../../../config/env';
import type { UnifiedPrompt } from '../hooks/use-prompt-source';

const MARKETPLACE_BASE_URL = `${SITE_BASE_URL}/marketplace`;

interface PublishDialogProps {
  open: boolean;
  prompt: UnifiedPrompt;
  onClose: () => void;
  onPublish: (
    input: PublishMarketplacePromptInput,
  ) => Promise<ApiResult<{ prompt: MarketplacePromptResponse }>>;
}

function listingUrl(slug: string): string {
  return `${MARKETPLACE_BASE_URL}/prompts/${slug}`;
}

export default function PublishDialog({
  open,
  prompt,
  onClose,
  onPublish,
}: Readonly<PublishDialogProps>): JSX.Element | null {
  const [categoryId, setCategoryId] = useState<MarketplaceCategoryId | ''>('');
  const [description, setDescription] = useState('');
  const [tagDraft, setTagDraft] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [publishedPrompt, setPublishedPrompt] = useState<MarketplacePromptResponse | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setCategoryId('');
    setDescription('');
    setTagDraft('');
    setTags([]);
    setError(null);
    setIsSubmitting(false);
    setPublishedPrompt(null);
  }, [open, prompt.id]);

  const descriptionCount = description.length;
  const listingHref = publishedPrompt ? listingUrl(publishedPrompt.slug) : null;
  const isPublishDisabled = useMemo(
    () => !categoryId || !description.trim() || isSubmitting,
    [categoryId, description, isSubmitting],
  );

  const pushTag = () => {
    const nextTag = tagDraft.trim().toLowerCase();

    if (!nextTag) {
      return;
    }

    if (nextTag.length > 30) {
      setError('Tags must be 30 characters or fewer.');
      return;
    }

    if (tags.includes(nextTag)) {
      setTagDraft('');
      return;
    }

    if (tags.length >= 5) {
      setError('You can add up to 5 tags.');
      return;
    }

    setTags((current) => [...current, nextTag]);
    setTagDraft('');
    setError(null);
  };

  const handleTagKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' && event.key !== ',') {
      return;
    }

    event.preventDefault();
    pushTag();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!categoryId || !description.trim()) {
      setError('Category and description are required.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const result = await onPublish({
      sourcePromptId: prompt.id,
      categoryId,
      description: description.trim(),
      tags,
    });

    setIsSubmitting(false);

    if (!result.success) {
      setError(result.error.message ?? result.error.error);
      return;
    }

    setPublishedPrompt(result.data.prompt);
  };

  return (
    <Dialog open={open}>
      <DialogOverlay onClick={onClose} />
      <DialogContent role="dialog" aria-modal="true" aria-label="Publish prompt to marketplace">
        {publishedPrompt && listingHref ? (
          <div className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>Published to Marketplace</DialogTitle>
              <DialogDescription>
                Your prompt is live and ready to share from the Claudafinil marketplace.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border border-border/70 bg-background/60 p-3">
              <p className="m-0 text-sm font-semibold text-foreground">{publishedPrompt.title}</p>
              <p className="mt-1 break-all text-xs text-muted-foreground">{listingHref}</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button
                type="button"
                onClick={() => window.open(listingHref, '_blank', 'noopener,noreferrer')}
              >
                View Listing
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Publish to Marketplace</DialogTitle>
              <DialogDescription>
                Share this cloud prompt publicly. The current title and content are published as a
                snapshot.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border border-border/70 bg-background/60 p-3">
              <p className="m-0 text-xs font-medium uppercase tracking-[0.08em] text-muted-foreground">
                Title
              </p>
              <p className="mt-1 text-sm font-semibold text-foreground">{prompt.title}</p>
            </div>

            <div className="flex flex-col gap-[0.35rem]">
              <Label htmlFor="publish-marketplace-category">Category</Label>
              <Select
                id="publish-marketplace-category"
                value={categoryId}
                onChange={(event) => {
                  setCategoryId(event.target.value as MarketplaceCategoryId);
                  setError(null);
                }}
              >
                <option value="">Select a category...</option>
                {MARKETPLACE_CATEGORIES.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="flex flex-col gap-[0.35rem]">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="publish-marketplace-description">Description</Label>
                <span className="text-[11px] text-muted-foreground">{descriptionCount}/500</span>
              </div>
              <Textarea
                id="publish-marketplace-description"
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setError(null);
                }}
                maxLength={500}
                rows={4}
                placeholder="Explain what this prompt does and when to use it."
              />
            </div>

            <div className="flex flex-col gap-[0.35rem]">
              <Label htmlFor="publish-marketplace-tag">Tags</Label>
              <div className="flex gap-2">
                <Input
                  id="publish-marketplace-tag"
                  value={tagDraft}
                  onChange={(event) => {
                    setTagDraft(event.target.value);
                    setError(null);
                  }}
                  onKeyDown={handleTagKeyDown}
                  maxLength={30}
                  placeholder="Add a tag and press Enter"
                />
                <Button type="button" variant="outline" onClick={pushTag} disabled={!tagDraft.trim()}>
                  Add
                </Button>
              </div>
              {tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="info"
                      className="gap-1 pr-1"
                    >
                      <span>{tag}</span>
                      <button
                        type="button"
                        aria-label={`Remove ${tag}`}
                        className="rounded-full p-0.5 text-current transition-opacity hover:opacity-80"
                        onClick={() => setTags((current) => current.filter((candidate) => candidate !== tag))}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="m-0 text-[11px] text-muted-foreground">
                  Up to 5 tags, 30 characters each.
                </p>
              )}
            </div>

            {error ? <p className="m-0 text-xs text-destructive">{error}</p> : null}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPublishDisabled}>
                {isSubmitting ? 'Publishing...' : 'Publish'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
